import countries from '@/ui/countries.json'

type CountryItem = {
  name: string
  code: string
}

const COUNTRIES = countries as CountryItem[]

export function localizeCountryCode(
  iso2: string | undefined,
  locale: string
): string {
  const code = iso2?.trim().toUpperCase() ?? ''
  if (code.length !== 2) {
    return ''
  }

  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) || code
  } catch {
    return code
  }
}

export function localizeCountryName(
  countryName: string | undefined,
  locale: string
): string {
  const raw = countryName?.trim()
  if (!raw) {
    return ''
  }

  // A country is stored as its English name; an older record may hold the
  // ISO2 code (BAT-1166).
  const country = COUNTRIES.find(
    c =>
      c.name.trim().toLowerCase() === raw.toLowerCase() ||
      c.code.toUpperCase() === raw.toUpperCase()
  )
  const iso2 = country?.code?.trim().toUpperCase() ?? ''
  if (iso2.length !== 2) {
    return raw
  }

  return localizeCountryCode(iso2, locale) || raw
}

/**
 * «City, Region, Country» of a stored location, the country in the UI
 * language. The region (BAT-835) tells same-named cities apart
 * («Springfield, Illinois»); it is left out without a city and when it only
 * repeats the city or the country («Singapore, Singapore»).
 */
export function formatLocation(
  city: string | undefined | null,
  country: string | undefined | null,
  locale: string,
  region?: string | null
): string {
  const cityName = city?.trim() ?? ''
  const countryName = localizeCountryName(country ?? undefined, locale)
  const regionName = region?.trim() ?? ''
  const repeats = [cityName, country?.trim() ?? '', countryName].map(name =>
    name.toLowerCase()
  )
  const showRegion =
    cityName !== '' &&
    regionName !== '' &&
    !repeats.includes(regionName.toLowerCase())
  return [cityName, showRegion ? regionName : '', countryName]
    .filter(Boolean)
    .join(', ')
}
